import React, { useState, useEffect, useRef, useMemo } from "react";
import axios from 'axios';
import { getCookie } from "../jwt/_helpers/cookie";
import { isDemoMode, isBrowserSessionDemo } from "../utils/demoConfig";
import DropboxIconSvg from "../assets/images/dropbox_icon.svg";
import "./dropboxChooser.css";

const API_URL = process.env.REACT_APP_API_URL_BASE;
const X_API_KEY = process.env.REACT_APP_X_API_KEY;
const WEB_SOCKET_URL = process.env.REACT_APP_WEB_SOCKET_URL;

const generateUploadID = () => {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

const generateRandomHex = (length) => {
    return [...Array(length)]
    .map(() => Math.floor(Math.random() * 16).toString(16))
    .join('');
}

const getFilenameFromLink = (url) => {
    const lastDot = url.lastIndexOf('.');
    const fileExtension = url.substring(lastDot + 1);
    const fileName = generateRandomHex(40);
    return `${fileName}.${fileExtension}`;
}

const getProgressMessage = (currentProgress) => {
    switch (true) {
        case (currentProgress >= 0 && currentProgress < 50):
            return 'Downloading File From Dropbox';
        case (currentProgress >= 50 && currentProgress < 90):
            return 'Processing File';
        case (currentProgress >= 90 && currentProgress < 91):
            return 'Analyzing Metadata';
        case (currentProgress >= 91 && currentProgress <= 100):
            return 'Finalizing Upload';
        default:
            return '';
    }
  }


const DropboxChooser = ({keyUploading, handleDropboxUploadComplete, handleFileMetadataError, fileType, acceptedExtensions, s3Path, clearArtworkFile=null, dolbyAtmos=false}) => {
    const dropboxDisabledInDemo = useMemo(
        () => isDemoMode() || isBrowserSessionDemo(),
        [],
    );
    const mountedRef = useRef(true);
    const wsRef = useRef(null);
    const token = getCookie().token;
    const options = {
      method: "POST",
      mode: 'cors',
      headers: {
        Authorization: `Bearer ${token}`,
        "x-api-key": X_API_KEY,
        "Content-Type": "application/json"
      }
    };
    const [progress, setProgress] = useState(0);
    const [connectionId, setConnectionId] = useState(null);
    const [transferLink, setTransferLink] = useState(null);
    const [fileMetadata, setFileMetadata] = useState(null);
    const [fileMetadataError, setFileMetadataError] = useState(false);
    const [S3Key, setS3Key] = useState(null);
    const [transferStarted, setTransferStarted] = useState(false);
    const prevProgressRef = useRef();

    const closeWebSocket = () => {
        if (!wsRef.current) {
            return;
        }
        const ws = wsRef.current;
        wsRef.current = null;
        try {
            ws.onopen = null;
            ws.onmessage = null;
            ws.onclose = null;
            ws.onerror = null;
            ws.close();
        } catch (e) {
            /* ignore */
        }
    };

    const initializeWebSocket = () => {
        closeWebSocket();
        const ws = new WebSocket(WEB_SOCKET_URL);
        wsRef.current = ws;

        ws.onopen = (event) => {
            if (!mountedRef.current) {
                return;
            }
            console.log("WebSocket connection opened:", event);

            const messageToSend = {
                action: 'clientMessage',
                data: 'Hello from client!'
            };
            ws.send(JSON.stringify(messageToSend));
        };

        ws.onmessage = (event) => {
            if (!mountedRef.current) {
                return;
            }
            console.log("WebSocket message received:", event.data);
            const data = JSON.parse(event.data);
            switch (data.action) {
                case "progressUpdate":
                    let percentCompleted = Math.round(data.progress);
                    let previousProgress = prevProgressRef.current || 0;        
                    if (percentCompleted > previousProgress && percentCompleted<=100) {
                        setProgress(percentCompleted);
                    }
                    break;
                case "acknowlement":
                    setConnectionId(data.connectionId);
                    break;
                case "metadata":
                    setFileMetadata(data.metadata);
                    console.log("File Metadata", data.metadata);
                    break;
                case "metadata_error":
                    setFileMetadataError(true);
                    console.log("Metadata Error", data.message);
                    break;
                default:
                    console.log("Unhandled action:", data.action);
            }
        };

        ws.onclose = (event) => {
            console.log("WebSocket connection closed:", event);
            if (mountedRef.current) {
                setTransferStarted(false);
            }
        };

        ws.onerror = (error) => {
            console.error("WebSocket error observed:", error);
            if (mountedRef.current) {
                setTransferStarted(false);
            }
        };
    };


    useEffect(() => {
        if (dropboxDisabledInDemo) {
            return;
        }
        if(transferLink && connectionId)
        {
            handleTransfer(transferLink)
        }
    }, [transferLink, connectionId])

    useEffect(() => {
        if (dropboxDisabledInDemo) {
            return undefined;
        }
        mountedRef.current = true;
        try {
            axios.post(`${API_URL}dropbox-upload/`, {"request_type": "warm-up"}, options);
        } catch (error) {
            console.error("Error during warm up:", error);
        }

        initializeWebSocket();

        return () => {
            mountedRef.current = false;
            closeWebSocket();
        };
    }, [dropboxDisabledInDemo])

    useEffect(() => {
        if (dropboxDisabledInDemo) {
            return;
        }
        if(fileMetadata && progress>=100 && mountedRef.current)
        {
            handleDropboxUploadComplete(S3Key, keyUploading, transferLink, fileMetadata, dolbyAtmos? 'dolby': '')
        }
    }, [S3Key, fileMetadata, progress])

    useEffect(() => {
        if (dropboxDisabledInDemo) {
            return;
        }
        if (!mountedRef.current) {
            return;
        }
        let currentProgress = Math.round(progress);
        let previousProgress = prevProgressRef.current || 0;

        if (currentProgress > previousProgress && currentProgress<=100) {
            if(dolbyAtmos) {
                const bar = document.getElementById(`uploadProgress_da_${keyUploading}`);
                const txt = document.getElementById(`uploadProgress_text_da_${keyUploading}`);
                if (bar) bar.style.width = currentProgress + '%';
                if (txt) txt.innerHTML = currentProgress + '%';
            }
            else {
                const bar = document.getElementById(`uploadProgress_${keyUploading}`);
                const txt = document.getElementById(`uploadProgress_text_${keyUploading}`);
                if (bar) bar.style.width = currentProgress + '%';
                if (txt) txt.innerHTML = currentProgress + '%';
            }
            const message = getProgressMessage(currentProgress);
            const msgEl = document.getElementById(`uploadProgressMessage_${keyUploading}`);
            if (msgEl) msgEl.innerHTML = message;
            prevProgressRef.current = currentProgress;
        }

        if(progress>=100)
        {
            setTransferStarted(false)
            setTransferLink(null);
        }
    }, [progress, dropboxDisabledInDemo])

    useEffect(() => {
        if (dropboxDisabledInDemo) {
            return;
        }
        if(fileMetadataError && mountedRef.current) {
            handleFileMetadataError(fileMetadataError, keyUploading);
        }
    }, [fileMetadataError])


    const startTransfer = async (fileLink, s3Key, uid, connectionId, fileType) => {
        const payload = {
            uid: uid,
            dropbox_link: fileLink,
            s3_key: s3Key,
            connection_id: connectionId,
            file_type: fileType,
            request_type: "transfer",
        };

        const progressInterval = setInterval(() => {
            setProgress(prevProgress => {
                if (prevProgress >= 40) {
                    return prevProgress;
                }
                return prevProgress + 0.5;
            });
        }, 500);
    
        try {
            const response = await axios.post(`${API_URL}dropbox-upload/`, payload, options);
            clearInterval(progressInterval);
            if (!mountedRef.current) {
                return;
            }
            console.log("Transfer response:", response.data);
        } catch (error) {
            clearInterval(progressInterval);
            if (!mountedRef.current) {
                return;
            }
            console.error("Error during transfer:", error);
        }
    };


    const handleTransfer = async (fileLink) => {
        const uid = generateUploadID();
        const fileName = getFilenameFromLink(transferLink);
        const s3Key = `${s3Path}/${fileName}`;
        setS3Key(s3Key);
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            console.warn("Dropbox WebSocket is not open; aborting transfer.");
            if (mountedRef.current) {
                setTransferStarted(false);
            }
            return;
        }
        startTransfer(fileLink, `media/${s3Key}`, uid, connectionId, fileType);
    };

    const handleFileSelect = (files) => {
        if(fileType==="image" && clearArtworkFile) {
            clearArtworkFile();
        }
        if(dolbyAtmos) {
            const bar = document.getElementById(`uploadProgress_da_${keyUploading}`);
            const txt = document.getElementById(`uploadProgress_text_da_${keyUploading}`);
            if (bar) bar.style.width = '0%';
            if (txt) txt.innerHTML = '0%';
        } else {
            const bar = document.getElementById(`uploadProgress_${keyUploading}`);
            const txt = document.getElementById(`uploadProgress_text_${keyUploading}`);
            if (bar) bar.style.width = '0%';
            if (txt) txt.innerHTML = '0%';
        }
        setFileMetadataError(false);
        setProgress(0);
        prevProgressRef.current = 0;
        setTransferStarted(true);
        setTransferLink(files[0].link);
    }


    const handleClick = () => {
        if (window.Dropbox) {
            window.Dropbox.choose({
                success: handleFileSelect,
                cancel: () => { },
                linkType: "direct",
                multiselect: false,
                extensions: acceptedExtensions,
            });
        } else {
            console.warn("Dropbox SDK hasn't loaded yet.");
        }
    };


    return (
        <div style={{marginBottom: "15px"}}>
            <center>
            {transferStarted && <div id={`uploadProgressMessage_${keyUploading}`} style={{color: "$skin1", fontWeight: "bold", paddingTop: "15px", paddingBottom: "15px"}}>Downloading File From Dropbox</div>}
            {!transferStarted &&
                <span
                    className={dropboxDisabledInDemo ? "dropbox-tooltip-wrapper" : ""}
                    data-tooltip={dropboxDisabledInDemo ? "This feature isn't available in the demo version." : ""}
                >
                    <button
                        type="button"
                        className="dropbox-button"
                        onClick={handleClick}
                        disabled={transferStarted || dropboxDisabledInDemo}
                    >
                        <img src={DropboxIconSvg} alt="Dropbox Logo" /> Choose from Dropbox
                    </button>
                </span>
            }
            </center>
        </div>
    );
}

export default DropboxChooser;
